<template>
  <div class="input-change-password">
    <slot name="txtBefore"></slot>
    <SimonModal
      :is-visible="show_"
      @hide="show_ = false"
      id="password-change-modal"
      size="large"
      show-footer-buttons
      @btnLeft="back"
      @btnRight="next"
      :loading-right-btn="loading"
      btn-right-title="Speichern"
    >
      <template v-slot:modalTitle>
          <h1><slot name="editPasswordTitle"></slot></h1>
      </template>
      <b-form
        class="change-password-input-form"
        :id="'form' + _uid">
        <div>
          <b-row>
            <b-col>
              <b-form-group :class="vflowGetFeedbackClasses('oldPassword')"  class="old-password password-group mandatory">
                <label for="oldPassword">
                  <slot name="oldPasswordLabelTxt">Altes Passwort</slot>
                </label>
                <b-form-input
                  @blur="vflowInputBlurred('oldPassword');"
                  @focus="vflowInputFocused('oldPassword');"
                  @input="$v.oldPassword.$touch(); onInput();"
                  name="oldPassword"
                  id="oldPassword"
                  ref="oldPassword"
                  required
                  :type="passwordType"
                  v-model="oldPassword"
                  autocomplete="off"
                ></b-form-input>
                <img :src="passwordIcon" alt="show" height="24" width="24" class="icon" @click="togglePasswordVisibility()" />
              </b-form-group>
            </b-col>
          </b-row>
        </div>
        <b-form-invalid-feedback v-if="oldPasswordInvalid">
          <slot name="txtInvalidOldPwLiveFeedback">Nicht Korrekt</slot>
        </b-form-invalid-feedback>
        <password-with-repeat
          ref="passwordInput"
          :password.sync="password"
          :repeat.sync="passwordRepeat"
          :invalid.sync="passwordInvalid"
          :on-pw-input="onInput"
          :on-repeat-input="onInput"
        >
          <template v-for="slot in possibleChildSlotsFor('Repeat')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </password-with-repeat>
      </b-form>
      <div v-if="!oldPasswordInvalid" class="errorMessage">
        <MessageModal/>
      </div>
      <slot name="txtAfter"></slot>
    </SimonModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import showHidePassword from '@/mixins/showHidePassword'
import { required, sameAs } from 'vuelidate/lib/validators'
import isValid from '@/mixins/validators/isValid'
import validationFlow from '@/mixins/validators/validationFlow'
import passwordValidator from '@/mixins/validators/passwordValidator'
import { BForm, BFormGroup, BFormInput } from 'bootstrap-vue'
import { validationMixin } from 'vuelidate'
import PasswordWithRepeat from '@/components/generic/PasswordWithRepeat.vue'
import syncProperty from '@/mixins/syncProperty'
import MessageModal from '@/components/generic/MessageView/MessageList.vue'
import transaction from '@/mixins/transaction'
import SimonModal from '@/components/modal/SimonModal.vue'

export default {
  name: 'InputChangePassword',
  data () {
    return {
      loading: false,
      oldPassword: '',
      oldPasswordInvalid: false,
      password: '',
      passwordRepeat: '',
      passwordInvalid: false
    }
  },
  components: {
    SimonModal,
    MessageModal,
    PasswordWithRepeat,
    BForm,
    BFormGroup,
    BFormInput
  },
  mixins: [
    validationMixin,
    isValid,
    validationFlow,
    passwordValidator,
    transaction('changePassword'),
    syncProperty('show', 'show_'),
    showHidePassword()
  ],
  mounted () {
    this.resetInput()
  },
  computed: {
    isIosDevice () {
      return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    }
  },
  methods: {
    ...mapActions('changePassword', [
      'changePassword'
    ]),
    onInput () {
      this.$store.commit('clearMessages', {}, { root: true })
    },
    resetInput () {
      this.$store.commit('clearMessages', {}, { root: true })
      this.vflowResetFeedbackStates()
      this.oldPassword = ''
      this.password = ''
      this.passwordRepeat = ''
    },
    next () {
      this.vflowForceFeedback()
      if (this.$refs.passwordInput) {
        this.$refs.passwordInput.vflowForceFeedback()
      }
      if (!this.passwordInvalid) {
        this.loading = true
        const data = {
          information_action: 'submit',
          information_name: 'edit password',
          information_ui_type: 'overlay',
          information_trigger: 'icon'
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('information', data)
        }
        this.changePassword({ oldPassword: this.oldPassword, newPassword: this.password })
          .then((response) => {
            if (response?.data?.changePassword?.success === true) {
              this.oldPasswordInvalid = false
              this.show_ = false
              this.password = ''
              this.passwordRepeat = ''
              this.oldPassword = ''
              if (window.App !== undefined && window.App.reset !== undefined) {
                if (this.isIosDevice) {
                  window.webkit.messageHandlers.reset.postMessage('changePassword')
                } else {
                  window.App.reset('changePassword')
                }
              } else {
                this.successToast(this.$slots.txtToastChanged ? this.$slots.txtToastChanged[0].text : 'Das Passwort wurde erfolgreich geändert.')
              }
            }
            this.loading = false
          }, () => {
            this.loading = false
          })
      } else {
        this.loading_ = false
        // ensure live feedback for validation errors
        this.vFlowGetFirstErrorElementName().then(function (result) {
          const data = {
            type: 'validation',
            message: 'required field missing',
            location: result + ' - unknown'
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        })
      }
    },
    back () {
      this.password = ''
      this.passwordRepeat = ''
      this.oldPassword = ''
      this.$store.commit('clearMessages', {}, { root: true })
      this.show_ = false
    }
  },
  validations () {
    return {
      oldPassword: {
        required
      },
      password: {
        required,
        passwordValidation (v) {
          return this.validatePassword(v)
        }
      },
      passwordRepeat: {
        required,
        sameAsPassword: sameAs(function () {
          return this.password
        })
      }
    }
  }
}
</script>
